import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";

/**
 * ColumnFilterDropdown
 * ─────────────────────────────────────────────────────────────────────────────
 * Dropdown simplificado: Solo lista de checkboxes + Botón "Aplicar Filtros" plano.
 * El color del texto coincide con el botón "Buscar" (#1403fa).
 */
export const ColumnFilterDropdown = ({
  colKey,
  label,
  options = [],
  activeValues = [],
  onApply,
  isActive,
}) => {
  const [open,     setOpen]     = useState(false);
  const [selected, setSelected] = useState(activeValues);
  const [pos,      setPos]      = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  /* Sincronizar valores externos cuando se aplican filtros o cambian los datos */
  useEffect(() => {
    setSelected(activeValues);
  }, [activeValues]);

  /* Cerrar al hacer clic fuera */
  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        // Al cerrar sin aplicar, revertimos a los valores activos
        setSelected(activeValues); 
      }
    };
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, activeValues]);

  /* Las opciones pueden ser strings o { value, label } */
  const normalizedOptions = options.map((opt) =>
    typeof opt === "object" && opt !== null ? opt : { value: opt, label: opt }
  );

  /* El dropdown usa position: fixed para no quedar recortado por contenedores con overflow;
     se cierra si la página/tabla hace scroll (salvo el scroll interno de su propia lista). */
  useEffect(() => {
    if (!open) return undefined;
    const closeOnScroll = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      setOpen(false);
      setSelected(activeValues);
    };
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [open, activeValues]);

  const toggleOption = (val) => {
    setSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleApply = () => {
    onApply(colKey, selected);
    setOpen(false);
  };

  const handleOpen = () => {
    setSelected(activeValues); // Resetear a valores aplicados al abrir
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const ANCHO_MAX = 270;
      setPos({
        top:  rect.bottom + 6,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - ANCHO_MAX)),
      });
    }
    setOpen((o) => !o);
  };

  return (
    <div ref={ref} style={styles.wrapper}>

      {/* ── Encabezado de columna ─────────────────────────────────────── */}
      <div
        style={{
          ...styles.headerBtn,
          color:      isActive ? "#1403fa" : "#333",
          fontWeight: isActive ? 700 : 600,
        }}
        onClick={handleOpen}
      >
        <span className="font-gothan-medium">{label}</span>
        <span
          style={{
            fontSize:   "10px",
            marginLeft: "4px",
            color:      isActive ? "#1403fa" : "#888",
            transition: "transform 0.15s",
            display:    "inline-block",
            transform:  open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <FontAwesomeIcon icon={faFilter} style={{ color: isActive ? "#1403fa" : "#000" }} />
        </span>
      </div>

      {/* ── Dropdown Simplificado ─────────────────────────────────────── */}
      {open && (
        <div style={{ ...styles.dropdown, position: "fixed", top: pos.top, left: pos.left }}>

          {/* Lista de opciones (Directamente, sin "Seleccionar todo") */}
          <div style={styles.optionList}>
            {normalizedOptions.length === 0 ? (
              <div style={styles.noOptions}>Sin opciones</div>
            ) : (
              normalizedOptions.map((opt) => (
                <label key={opt.value} style={styles.checkRow}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggleOption(opt.value)}
                    style={styles.checkbox}
                  />
                  <span style={styles.optionLabel}>{opt.label}</span>
                </label>
              ))
            )}
          </div>

          <div style={styles.divider} />

          {/* Botón Aplicar (Estilo Plano / Sin fondo / Color #1403fa) */}
          <div style={styles.applyRow}>
            <button
              onClick={handleApply}
              style={styles.applyBtn}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
 * TableWebBodyComponent
 * ─────────────────────────────────────────────────────────────────────────────
 */
const TableWebBodyComponent = ({
  columns          = [],
  filas            = [],
  sortKey,
  sortDir,
  onSort,
  columnFilters    = {},
  getColumnOptions,
  onColumnFilter,
  hasActiveFilter,
  loading          = false,
}) => {
  return (
    <table className="table table-hover">

      {/* ── ENCABEZADO ─────────────────────────────────────────────────── */}
      <thead className="listar-thead">
        <tr className="bg-cl-gris">
          {columns.map((col) => {
            const isFilterable = col.filterable && getColumnOptions && onColumnFilter;
            const isSortable   = col.sortable && !isFilterable;
            const isActive     = hasActiveFilter ? hasActiveFilter(col.key) : false;

            return (
              <th
                key={col.key}
                style={{
                  textAlign:        col.align || "left",
                  padding:          "10px 12px",
                  fontWeight:       600,
                  borderBottom:     "2px solid #e5e7eb",
                  whiteSpace:       "nowrap",
                  position:         "relative",
                  cursor:           isSortable ? "pointer" : "default",
                  userSelect:       "none",
                  backgroundColor:  isActive ? "#eef0ff" : undefined,
                }}
                onClick={() => isSortable && onSort && onSort(col.key)}
              >
                {isFilterable ? (
                  <ColumnFilterDropdown
                    colKey={col.key}
                    label={col.label}
                    options={getColumnOptions(col.key)}
                    activeValues={columnFilters[col.key] || []}
                    onApply={onColumnFilter}
                    isActive={isActive}
                  />
                ) : (
                  <div
                    className="flex font-gothan-medium"
                    style={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    {col.label}
                    {isSortable && sortKey === col.key && (
                      <span style={{ fontSize: "10px", color: "#1403fa" }}>
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      </thead>

      {/* ── CUERPO ─────────────────────────────────────────────────────── */}
      <tbody className="listar-tbody">
        {loading ? (
          <tr>
            <td colSpan={columns.length} style={styles.centerCell}>
              Cargando datos...
            </td>
          </tr>
        ) : filas.length === 0 ? (
          <tr>
            <td colSpan={columns.length} style={styles.centerCell}>
              No se encontraron registros.
            </td>
          </tr>
        ) : (
          filas.map((row, rowIndex) => (
            <tr
              key={row.Id || row.id || rowIndex}
              style={{ borderBottom: "1px solid #e5e7eb" }}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding:    "10px 12px",
                    textAlign:  col.align || "left",
                    whiteSpace: "nowrap",
                    color:      "#444",
                    fontSize:   "13px",
                  }}
                >
                  {col.render
                    ? col.render(row)
                    : row[col.key] !== null &&
                      row[col.key] !== undefined &&
                      row[col.key] !== ""
                      ? row[col.key]
                      : "—"
                  }
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

/* ── Estilos ─────────────────────────────────────────────────────────────── */
const styles = {
  wrapper: {
    position:  "relative",
    display:   "inline-block",
    minWidth:  "80px",
  },
  headerBtn: {
    display:    "flex",
    alignItems: "center",
    gap:        "4px",
    cursor:     "pointer",
    userSelect: "none",
    padding:    "2px 0",
  },
  dropdown: {
    position:     "absolute",
    top:          "calc(100% + 6px)",
    left:         "0",
    zIndex:       1000,
    background:   "#ffffff",
    border:       "1px solid #e5e7eb",
    borderRadius: "6px",
    boxShadow:    "0 4px 16px rgba(0,0,0,0.12)",
    minWidth:     "200px",
    maxWidth:     "260px",
    paddingBottom: "4px",
  },
  optionList: {
    maxHeight: "240px",
    overflowY: "auto",
    padding:   "4px 0",
  },
  checkRow: {
    display:      "flex",
    alignItems:   "center",
    gap:          "8px",
    padding:      "6px 14px",
    cursor:       "pointer",
    fontSize:     "13px",
    whiteSpace:   "nowrap",
    overflow:     "hidden",
    textOverflow: "ellipsis",
  },
  checkbox: {
    accentColor: "#1403fa",
    width:       "15px",
    height:      "15px",
    flexShrink:  0,
    cursor:      "pointer",
  },
  optionLabel: {
    fontSize:     "13px",
    color:        "#333",
    overflow:     "hidden",
    textOverflow: "ellipsis",
    whiteSpace:   "nowrap",
    fontWeight:   500,
  },
  divider: {
    height:     "1px",
    background: "#f0f0f0",
    margin:     "4px 0",
  },
  applyRow: {
    padding: "4px 14px 8px",
  },
  applyBtn: {
    width:           "100%",
    padding:         "8px 0",
    backgroundColor: "transparent",
    color:           "#1403fa", // ← Color exacto del botón Buscar
    border:          "none",
    borderRadius:    "4px",
    fontSize:        "13px",
    fontWeight:      700,
    cursor:          "pointer",
    transition:      "background-color 0.15s",
  },
  noOptions: {
    padding:   "10px 14px",
    fontSize:  "13px",
    color:     "#9ca3af",
    textAlign: "center",
  },
  centerCell: {
    textAlign: "center",
    padding:   "32px 20px",
    color:     "#6b7280",
    fontSize:  "13px",
  },
};

export default TableWebBodyComponent;